import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); } catch {}
    setSent(true); setLoading(false);
  };

  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a link to reset it">
      {sent ? (
        <div className="text-center">
          <p className="rounded-md bg-blue-50 px-4 py-3 text-sm text-slate-700" data-testid="forgot-confirm">
            If that email is registered, a reset link has been sent. Please check your inbox.
          </p>
          <Link to="/login" className="mt-4 inline-block text-sm text-blue-800 hover:underline">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
          </div>
          <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900" disabled={loading} data-testid="forgot-submit">
            {loading ? "Sending..." : "Send reset link"}
          </Button>
          <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-blue-800">Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Set a new password" subtitle="Enter your new password below">
      {done ? (
        <p className="rounded-md bg-green-50 px-4 py-3 text-center text-sm text-green-700" data-testid="reset-success">
          Password reset successful. Redirecting to sign in...
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="reset-error">{error}</p>}
          <div>
            <Label htmlFor="password">New Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password" />
          </div>
          <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900" disabled={loading || !token} data-testid="reset-submit">
            {loading ? "Resetting..." : "Reset password"}
          </Button>
          <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-blue-800">Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="font-heading text-2xl font-bold text-slate-900">{title}</h1>
        <p className="mb-6 mt-1 text-sm text-slate-500">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}
